import { t } from 'i18next';
import { SubscriptionPlanTypes } from '../types/coach';
import { ClientLimitStatus } from '../redux/coach/coachSlice';

export const getPlans = () => ({
  free: {
    planType: SubscriptionPlanTypes.FREE,
    title: t('subscriptionPlans.freeTitle'),
    price: 0,
    features: ['freeUsersInfo', 'allFeatures'],
    rank: 0,
  },
  starter: {
    planType: SubscriptionPlanTypes.STARTER,
    title: t('subscriptionPlans.starterTitle'),
    price: 19,
    features: ['starterClients', 'allFeatures'],
    rank: 1,
  },
  pro: {
    planType: SubscriptionPlanTypes.PRO,
    title: t('subscriptionPlans.proTitle'),
    price: 59,
    features: ['unlimitedClients', 'allFeatures'],
    rank: 2,
  },
  ultimate: {
    planType: SubscriptionPlanTypes.ULTIMATE,
    title: t('subscriptionPlans.ultimateTitle'),
    price: 699,
    features: ['ultimateFreedom', 'everythingUnlimited'],
    rank: 3,
  },
});

export const formatPlanName = (name: string) => {
  if (name === SubscriptionPlanTypes.PREMIUM) {
    return t('subscriptionPlans.lifeTimePlan');
  }
  if (name === SubscriptionPlanTypes.FREE_TRIAL) {
    return t('subscriptionPlans.freeTrial');
  }
  return t(`subscriptionPlans.${name.toLowerCase()}Title`);
};

export const formatPrice = (price: number, name: SubscriptionPlanTypes) => {
  if (name === SubscriptionPlanTypes.PREMIUM) {
    return { pkgPrice: '$99', period: '' };
  }
  if (name === SubscriptionPlanTypes.FREE) {
    return { pkgPrice: t('subscriptionPlans.freePrice'), period: '' };
  }
  if (name === SubscriptionPlanTypes.ULTIMATE) {
    return { pkgPrice: `$${price}`, period: '' };
  }
  return { pkgPrice: `$${price}`, period: `/${t('subscriptionPlans.month')}` };
};

export const getPlanInfo = (plan: string) => {
  switch (plan) {
    case SubscriptionPlanTypes.FREE:
      return {
        text1: t('subscriptionPlans.features.freeUsersInfo'),
        text2: t('subscriptionPlans.features.allFeatures'),
      };
    case SubscriptionPlanTypes.STARTER:
      return {
        text1: t('subscriptionPlans.features.starterClients'),
        text2: t('subscriptionPlans.features.allFeatures'),
      };
    case SubscriptionPlanTypes.PRO:
      return {
        text1: t('subscriptionPlans.features.unlimitedClients'),
        text2: t('subscriptionPlans.features.allFeatures'),
      };
    case SubscriptionPlanTypes.ULTIMATE:
      return {
        text1: t('subscriptionPlans.features.ultimateFreedom'),
        text2: t('subscriptionPlans.features.everythingUnlimited'),
      };
    case SubscriptionPlanTypes.PREMIUM:
      return {
        text1: t('subscriptionPlans.features.unlimitedClients'),
        text2: t('subscriptionPlans.features.support'),
      };
    default:
      return undefined;
  }
};

export const getPlanRank = (plan?: SubscriptionPlanTypes | null) => {
  switch (plan) {
    case SubscriptionPlanTypes.NONE:
      return -1;
    case SubscriptionPlanTypes.FREE_TRIAL:
      return -2;
    case SubscriptionPlanTypes.PREMIUM:
      return -2;
    case SubscriptionPlanTypes.FREE:
      return 0;
    case SubscriptionPlanTypes.STARTER:
      return 1;
    case SubscriptionPlanTypes.PRO:
      return 2;
    case SubscriptionPlanTypes.ULTIMATE:
      return 3;
    default:
      return 0;
  }
};

export const showPaymentInfo = (
  currentPlan?: SubscriptionPlanTypes,
  previousPlan?: SubscriptionPlanTypes
) => {
  if (getPlanRank(currentPlan) < 0) {
    return false;
  }
  if (currentPlan === SubscriptionPlanTypes.FREE && getPlanRank(previousPlan) < 0) {
    return false;
  }
  return true;
};

export const showBanner = (
  clientCountStatus: ClientLimitStatus,
  paymentStatus: string,
  currentPlan: string,
  daysLeft: number | undefined
) =>
  clientCountStatus === ClientLimitStatus.ABOUT_TO_REACH ||
  clientCountStatus === ClientLimitStatus.LIMIT_REACHED ||
  paymentStatus === 'FAILURE' ||
  (typeof daysLeft === 'number' &&
    daysLeft < 7 &&
    currentPlan === SubscriptionPlanTypes.FREE_TRIAL);
